import graphql from "babel-plugin-relay/macro";
import { format, parse } from "date-fns";
import { useFragment } from "react-relay";

import {
  CalendarFilledIcon,
  ClockIcon,
  PlaceIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  HStack,
} from "@whoosh/design-system/src/components/primitive";

import { EventDetailInfo_eventSession$key } from "./__generated__/EventDetailInfo_eventSession.graphql";

type EventDetailInfoProps = {
  fragmentRef: EventDetailInfo_eventSession$key | null;
  attendeesInfo?: React.ReactNode;
};
export const EventDetailInfo = ({
  fragmentRef,
  attendeesInfo,
}: EventDetailInfoProps) => {
  const data = useFragment(EventDetailInfofragmentString, fragmentRef);

  const dateObj =
    data?.agenda.date && parse(data?.agenda.date, "yyyy-MM-dd", new Date());

  const formattedDateObj = dateObj && format(dateObj, "EEEE, MMM do");
  const formattedStartTime = data?.startAt
    ? format(parse(data.startAt, "HH:mm:ss", new Date()), "h:mm aaa")
    : "";

  const formattedEndTime = data?.endAt
    ? format(parse(data.endAt, "HH:mm:ss", new Date()), "h:mm aaa")
    : "";

  const initialCourses =
    data?.courses?.edges?.flatMap((e) => {
      return e?.node?.id ? [{ id: e.node.id, name: e.node.name }] : [];
    }) ?? [];

  const timeString = data?.displayTime
    ? data.displayTime
    : `${formattedStartTime}-${formattedEndTime}`;

  return (
    <VStack gap={2}>
      {/* Date */}
      <HStack gap={2} alignItems="center">
        <CalendarFilledIcon color="icon-primary" />
        <Text
          maxFontSizeMultiplier={2}
          variant="mainSmallSemiBold"
          fontWeight={600}
        >
          {formattedDateObj}
        </Text>
      </HStack>
      {/* Time */}

      <HStack gap={2} alignItems="center">
        <ClockIcon color="icon-primary" />
        <Text
          maxFontSizeMultiplier={2}
          variant="mainSmallSemiBold"
          fontWeight={600}
        >
          {timeString}
        </Text>
      </HStack>

      {/* Attendees */}

      {attendeesInfo}

      {/* Courses */}

      <HStack gap={2} alignItems="center">
        <PlaceIcon color="icon-primary" />
        <Text
          maxFontSizeMultiplier={2}
          variant="mainSmallSemiBold"
          fontWeight={600}
        >
          {initialCourses.map(
            (c, i) => `${c.name}${i < initialCourses.length - 1 ? ", " : ""}`
          )}
        </Text>
      </HStack>
    </VStack>
  );
};

export const EventDetailInfofragmentString = graphql`
  fragment EventDetailInfo_eventSession on EventSession {
    durationMins
    startAt
    endAt
    displayTime
    courses {
      edges {
        node {
          id
          name
          courseBucket {
            name
          }
        }
      }
    }
    agenda {
      date
    }
  }
`;
